import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogClose, DialogContent, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { cn } from '../lib/utils';
import { specsToCompare } from './comparisonUtils';
import { useFreezeBackground } from '../hooks/useFreezeBackground';
import './AirplaneCompare.css';

// Apple-style Minimalist Compare Component
const basicSpecs = [
  { key: 'max_range', label: 'Max Range', unit: 'km' },
  { key: 'cruise_speed', label: 'Cruise Speed', unit: 'km/h' },
  { key: 'max_passengers', label: 'Max Passengers', unit: 'pax' },
];

const detailHeroSpecs = [
  { key: 'max_range', label: 'Range', unit: 'km' },
  { key: 'cruise_speed', label: 'Cruise', unit: 'km/h' },
  { key: 'max_passengers', label: 'Capacity', unit: 'pax' },
];

const getNumericValue = (val) => {
  if (!val) return 0;
  if (typeof val === 'number') return val;
  const parsed = parseFloat(String(val).replace(/,/g, ''));
  return isNaN(parsed) ? 0 : parsed;
};

const formatSpecDisplay = (value, unit) => {
  if (value === null || value === undefined || value === '') {
    return 'N/A';
  }

  if (typeof value === 'number') {
    return `${value.toLocaleString()}${unit ? ` ${unit}` : ''}`;
  }

  const normalizedValue = String(value).trim();
  const numericValue = Number(normalizedValue.replace(/,/g, ''));

  if (!Number.isNaN(numericValue) && normalizedValue !== '') {
    return `${numericValue.toLocaleString()}${unit ? ` ${unit}` : ''}`;
  }

  return `${normalizedValue}${unit ? ` ${unit}` : ''}`;
};

const getPlaneDisplayName = (plane) => plane?.model || plane?.name || 'Unknown aircraft';
const getPlaneImageUrl = (plane) => plane?.image_url || '/images/airplane.png';

// Helper to determine if lower is better for specific metrics
const isLowerBetter = (key) => {
  const lowerIsBetterKeys = ['cabin_noise_level', 'runway_length'];
  return lowerIsBetterKeys.includes(key);
};

export default function AirplaneCompare({ planes = [] }) {
  const [plane1Id, setPlane1Id] = useState(null);
  const [plane2Id, setPlane2Id] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const scrollContainerRef = useRef(null);
  const closeButtonRef = useRef(null);

  useFreezeBackground(isDetailOpen);

  useEffect(() => {
    if (planes.length >= 2 && !plane1Id && !plane2Id) {
      setPlane1Id(planes[0].id);
      setPlane2Id(planes[1].id);
    }
  }, [planes, plane1Id, plane2Id]);

  useEffect(() => {
    if (!isDetailOpen) {
      return undefined;
    }

    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
    closeButtonRef.current?.focus();
    return undefined;
  }, [isDetailOpen]);

  if (planes.length < 2) return null;

  const plane1 = planes.find(p => p.id === plane1Id) || planes[0];
  const plane2 = planes.find(p => p.id === plane2Id) || planes[1];

  const handlePlane1Select = (id) => {
    if (id === plane2Id) {
      setPlane2Id(plane1Id);
    }
    setPlane1Id(id);
  };

  const handlePlane2Select = (id) => {
    if (id === plane1Id) {
      setPlane1Id(plane2Id);
    }
    setPlane2Id(id);
  };

  const renderHeroMetrics = (plane) => (
    <div className="ac-detail-hero-metrics">
      {detailHeroSpecs.map((spec) => (
        <div className="ac-detail-hero-metric" key={`${plane?.id || getPlaneDisplayName(plane)}-${spec.key}`}>
          <span className="ac-detail-hero-metric-label">{spec.label}</span>
          <span className="ac-detail-hero-metric-value">
            {formatSpecDisplay(plane?.[spec.key], spec.unit)}
          </span>
        </div>
      ))}
    </div>
  );

  const renderSpecRow = (spec, isDetail = false) => {
    const val1 = getNumericValue(plane1[spec.key]);
    const val2 = getNumericValue(plane2[spec.key]);
    
    let isVal1Better = false;
    let isVal2Better = false;
    
    if (val1 && val2 && val1 !== val2) {
      if (isLowerBetter(spec.key)) {
        isVal1Better = val1 < val2;
        isVal2Better = val2 < val1;
      } else {
        isVal1Better = val1 > val2;
        isVal2Better = val2 > val1;
      }
    }

    return (
      <div className={cn("ac-spec-row", isDetail && "ac-detail-row")} key={spec.key}>
        <div className={cn("ac-spec-value", isVal1Better && "ac-spec-value--better")}>
          {plane1[spec.key] || '-'} {spec.unit && plane1[spec.key] ? <span className="ac-spec-unit">{spec.unit}</span> : ''}
        </div>
        <div className="ac-spec-label">{spec.label}</div>
        <div className={cn("ac-spec-value", isVal2Better && "ac-spec-value--better")}>
          {plane2[spec.key] || '-'} {spec.unit && plane2[spec.key] ? <span className="ac-spec-unit">{spec.unit}</span> : ''}
        </div>
      </div>
    );
  };

  return (
    <section className="ac-container">
      <div className="ac-header">
        <h2 className="ac-title">Compare Aircraft.</h2>
        <p className="ac-subtitle">Which one is right for you?</p>
      </div>

      <div className="ac-grid">
        {/* Plane 1 */}
        <div className="ac-column">
          <div className="ac-image-wrapper">
            <img src={plane1.image_url} alt={plane1.name} className="ac-image" />
          </div>
          <div className="ac-selector-wrapper">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="ac-selector-btn">
                  {plane1.model || plane1.name} <span className="ac-chevron">⌄</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="ac-dropdown-content h-64 overflow-y-auto">
                {planes.map(p => (
                  <DropdownMenuItem key={p.id} onSelect={() => handlePlane1Select(p.id)} className={p.id === plane1Id ? 'font-bold' : ''}>
                    {p.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <p className="ac-manufacturer">{plane1.manufacturer}</p>
          </div>
        </div>

        {/* Plane 2 */}
        <div className="ac-column">
          <div className="ac-image-wrapper">
            <img src={plane2.image_url} alt={plane2.name} className="ac-image" />
          </div>
          <div className="ac-selector-wrapper">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="ac-selector-btn">
                  {plane2.model || plane2.name} <span className="ac-chevron">⌄</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="ac-dropdown-content h-64 overflow-y-auto">
                {planes.map(p => (
                  <DropdownMenuItem key={p.id} onSelect={() => handlePlane2Select(p.id)} className={p.id === plane2Id ? 'font-bold' : ''}>
                    {p.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <p className="ac-manufacturer">{plane2.manufacturer}</p>
          </div>
        </div>
      </div>

      <div className="ac-specs-container">
        {basicSpecs.map(spec => renderSpecRow(spec, false))}
      </div>

      <div className="ac-action-wrapper">
        <button className="ac-action-btn" onClick={() => setIsDetailOpen(true)}>
          Full Compare
        </button>
      </div>

      <Dialog open={isDetailOpen} onOpenChange={(nextOpen) => !nextOpen && setIsDetailOpen(false)}>
        <DialogContent
          disableDefaultAnimation
          overlayClassName="detail-dialog-overlay"
          className="detail-dialog detail-dialog--comparison ac-detail-dialog w-[min(96vw,1240px)] max-w-none overflow-hidden p-0 focus:outline-none focus-visible:outline-none h-[88vh] flex flex-col bg-background/95 backdrop-blur-xl"
        >
          <DialogTitle className="sr-only">Detailed Comparison</DialogTitle>
          
          <div
            ref={scrollContainerRef}
            className="ac-detail-scroll-container overflow-y-auto w-full h-full relative"
          >
            <div className="ac-detail-sticky-header">
              <div className="ac-detail-sticky-shell">
                <div className="ac-detail-sticky-plane">
                  <div className="ac-detail-sticky-thumb">
                    <img src={getPlaneImageUrl(plane1)} alt={plane1.name} className="ac-detail-sticky-thumb-image" />
                  </div>
                  <div className="ac-detail-sticky-copy">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="ac-selector-btn ac-detail-sticky-selector">
                          {getPlaneDisplayName(plane1)} <span className="ac-chevron">⌄</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="center" className="h-64 overflow-y-auto">
                        {planes.map(p => (
                          <DropdownMenuItem key={p.id} onSelect={() => handlePlane1Select(p.id)} className={p.id === plane1Id ? 'font-bold' : ''}>
                            {p.name}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <p className="ac-detail-sticky-meta">{plane1.manufacturer}</p>
                  </div>
                </div>

                <div className="ac-detail-sticky-center">
                  <span className="ac-detail-sticky-label">VS</span>
                  <span className="ac-detail-sticky-caption">Detailed compare</span>
                </div>

                <div className="ac-detail-sticky-plane ac-detail-sticky-plane--right">
                  <div className="ac-detail-sticky-copy">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="ac-selector-btn ac-detail-sticky-selector">
                          {getPlaneDisplayName(plane2)} <span className="ac-chevron">⌄</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="center" className="h-64 overflow-y-auto">
                        {planes.map(p => (
                          <DropdownMenuItem key={p.id} onSelect={() => handlePlane2Select(p.id)} className={p.id === plane2Id ? 'font-bold' : ''}>
                            {p.name}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <p className="ac-detail-sticky-meta">{plane2.manufacturer}</p>
                  </div>
                  <div className="ac-detail-sticky-thumb">
                    <img src={getPlaneImageUrl(plane2)} alt={plane2.name} className="ac-detail-sticky-thumb-image" />
                  </div>
                </div>
              </div>
            </div>

            <div className="ac-detail-content">
              <section className="ac-detail-hero" aria-label="Aircraft comparison hero">
                <article className="ac-detail-hero-card">
                  <div className="ac-detail-hero-image-wrap">
                    <img src={getPlaneImageUrl(plane1)} alt={plane1.name} className="ac-detail-hero-image" />
                  </div>
                  <div className="ac-detail-hero-copy">
                    <p className="ac-detail-hero-kicker">{plane1.manufacturer || 'Aircraft'}</p>
                    <h3 className="ac-detail-hero-title">{getPlaneDisplayName(plane1)}</h3>
                    <p className="ac-detail-hero-summary">{plane1.type || plane1.name}</p>
                    {renderHeroMetrics(plane1)}
                  </div>
                </article>

                <div className="ac-detail-hero-divider">
                  <span className="ac-detail-hero-divider-badge">VS</span>
                  <p className="ac-detail-hero-divider-text">性能、尺寸与客舱数据一屏对照</p>
                </div>

                <article className="ac-detail-hero-card">
                  <div className="ac-detail-hero-image-wrap">
                    <img src={getPlaneImageUrl(plane2)} alt={plane2.name} className="ac-detail-hero-image" />
                  </div>
                  <div className="ac-detail-hero-copy">
                    <p className="ac-detail-hero-kicker">{plane2.manufacturer || 'Aircraft'}</p>
                    <h3 className="ac-detail-hero-title">{getPlaneDisplayName(plane2)}</h3>
                    <p className="ac-detail-hero-summary">{plane2.type || plane2.name}</p>
                    {renderHeroMetrics(plane2)}
                  </div>
                </article>
              </section>

              {Object.entries(specsToCompare).map(([category, specs]) => (
                <div key={category} className="ac-detail-section mb-16">
                  <h3 className="ac-detail-section-title text-2xl font-semibold mb-8 text-center">{category}</h3>
                  <div className="ac-detail-section-body border-t border-border/50">
                    {specs.map(spec => renderSpecRow(spec, true))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <DialogClose asChild>
            <Button
              ref={closeButtonRef}
              type="button"
              variant="ghost"
              size="icon"
              className="detail-close-button absolute right-4 top-4 rounded-full shadow-md z-20 bg-background/50 backdrop-blur-md hover:bg-background"
              aria-label="Close"
            >
              <span className="text-lg">x</span>
            </Button>
          </DialogClose>
        </DialogContent>
      </Dialog>
    </section>
  );
}
